import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, TextField } from '@material-ui/core';

// La forma in cui il backend salva il MAC di una sonda (BSS-1900): dodici cifre
// esadecimali maiuscole, a coppie separate da due punti.
const FORMA_CANONICA = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;

// Gli stessi separatori che il normalizzatore del backend toglie dal `sn`
// (`DeviceIdentifierProperties`): una grafia che lui accetta, qui non e' un errore.
const SEPARATORI = /[\s:\-._]/g;

export const MESSAGGIO_MAC_NON_VALIDO =
  'Non è un MAC valido: servono 12 cifre esadecimali, con o senza separatori (es. AC:23:3F:AB:CD:12).';

/**
 * Il MAC in forma canonica, oppure `null` se, tolti separatori e spazi, non
 * restano esattamente dodici cifre esadecimali.
 */
export const normalizzaMac = (valore) => {
  const cifre = (valore ?? '').replace(SEPARATORI, '').toUpperCase();
  if (!/^[0-9A-F]{12}$/.test(cifre)) return null;
  return cifre.match(/.{2}/g).join(':');
};

/**
 * Campo di testo per il MAC di una sonda. Mentre si scrive non tocca niente;
 * alla perdita del focus, se il valore non e' gia' in forma canonica, prova a
 * normalizzarlo e, se non ci riesce, lo dice sotto il campo.
 *
 * La normalizzazione vera resta del backend, che rifiuta comunque un valore non
 * valido: qui serve a mostrare al tecnico, prima di salvare, la forma con cui la
 * sonda verra' registrata — e a fermarlo su un errore di battitura senza un giro
 * al server.
 *
 * `onBlur` riceve il valore dopo la normalizzazione e se e' un MAC valido, cosi'
 * il chiamante puo' confermarlo nello stesso gesto senza ricalcolarlo.
 */
const CampoMac = ({
  value,
  onChange,
  onBlur,
  disabled,
  placeholder,
  helperText,
  inputProps,
}) => {
  const [nonValido, setNonValido] = useState(false);

  const handleChange = (event) => {
    // L'errore si toglie appena il tecnico corregge, non al blur successivo: un
    // messaggio rosso sotto un valore che sta gia' cambiando non dice piu' niente.
    setNonValido(false);
    onChange(event.target.value);
  };

  const handleBlur = () => {
    const digitato = value.trim();
    if (digitato === '') {
      setNonValido(false);
      onBlur?.('', false);
      return;
    }
    if (FORMA_CANONICA.test(digitato)) {
      setNonValido(false);
      if (digitato !== value) onChange(digitato);
      onBlur?.(digitato, true);
      return;
    }
    const normalizzato = normalizzaMac(digitato);
    if (normalizzato) {
      setNonValido(false);
      onChange(normalizzato);
      onBlur?.(normalizzato, true);
      return;
    }
    setNonValido(true);
    onBlur?.(digitato, false);
  };

  return (
    <>
      <TextField
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        error={nonValido}
        onChange={handleChange}
        onBlur={handleBlur}
        inputProps={{
          'aria-invalid': nonValido,
          spellCheck: false,
          autoCapitalize: 'characters',
          ...inputProps,
        }}
      />
      {nonValido ? (
        <FormHelperText error>{MESSAGGIO_MAC_NON_VALIDO}</FormHelperText>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </>
  );
};

CampoMac.propTypes = {
  value: PropTypes.string.isRequired,
  // Riceve la stringa, non l'evento: il campo cambia valore anche da solo, al blur.
  onChange: PropTypes.func.isRequired,
  // (valore, valido) dopo la normalizzazione.
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  inputProps: PropTypes.object,
};

export default CampoMac;
